import AssetLoader from "@/components/AssetLoader/AssetLoader";
import PromiseLoader from "@/components/PromiseLoader/PromiseLoader";
import i18n from "@/i18n";
import { TableSort } from "@/model/common/CommonInterfaces";
import { ObjectId } from "@/model/db/General";
import BaseAsset from "@/model/general-assets/BaseAsset";
import DataBus from "@/services/DataBus";
import { MatchQuery } from "@/services/DataService";
import { hasValue, when } from "@/utils/Helpers";
import MQ from "@/utils/MatchQueryUtils";
import classNames from "classnames";
import { nanoid } from "nanoid";
import { useState } from "react";
import BFButton from "../../general/Button/BFButton";
import ValidationPopover, {
  ValidatorPopoverStyle,
} from "../../general/ValidationPopover/ValidationPopover";
import BFOverlay from "../../general/whisper/BFOverlay";
import BfIcon from "../../icon/BfIcon";
import { DefaultIcons } from "../../icon/DefaultIcons";
import LabeledInput from "../LabeledInput";
import BFAssetChooser, {
  BFAssetChooserGetOption,
  BFAssetChooserGetOptionPromise,
  BFAssetChooserOption,
} from "./BFAssetChooser";
import "./BFAssetChooserSelectMultiple.scss";

type BFAssetChooserSelectMultipleProps = (
  | BFAssetChooserGetOption
  | BFAssetChooserGetOptionPromise
) & {
  value: ObjectId[];
  onChange: (value: ObjectId[]) => void;
  validation?: {
    message: string;
    level: "error" | "warning";
  };
  validatorStyle?: ValidatorPopoverStyle;
  labelPosition?: "top" | "left";

  info?: React.ReactNode;
  label?: string;
  labelSuffix?: React.ReactNode;
  placeholder?: string;
  hideSubLabel?: boolean;

  overlayTopComponent?: (close: () => void) => React.ReactNode;
  overlayBottomComponent?: (close: () => void) => React.ReactNode;

  overlayClassName?: string;

  disabled?: boolean;
  sort?: TableSort[];
  overlayHeight?: string | number;
  groups?: {
    matchQuery: MatchQuery;
    label: string;
    value: string;
  }[];

  assetType: string;
  matchQuery?: MatchQuery;
  detailView?: (node: BaseAsset) => JSX.Element;
};
const BFAssetChooserSelectMultiple = (
  props: BFAssetChooserSelectMultipleProps
) => {
  const [id] = useState(nanoid());

  return (
    <div className={classNames("bf-asset-chooser-select-multiple")}>
      <LabeledInput
        label={props.label}
        info={props.info}
        labelPosition={props.labelPosition}
        suffix={props.labelSuffix}
        error={!!props.validation?.message}
      >
        <ValidationPopover
          validatorStyle={props.validatorStyle}
          level={props.validation ? props.validation.level : "error"}
          message={props.validation ? props.validation.message : null}
          marginTop={0}
        >
          <div className={`bf-asset-chooser-select-multiple-container`}>
            {props.value && (
              <div>
                {props.value.map((valueEntry) => (
                  <div className={`value-entry`}>
                    <AssetLoader
                      inline
                      assetType={props.assetType}
                      id={valueEntry}
                      render={(asset: BaseAsset) => {
                        const renderOption = (option: BFAssetChooserOption) => {
                          return (
                            <>
                              <div className={`value-text`}>
                                {option.subLabel && (
                                  <span className={`sub-label`}>
                                    {option.subLabel}
                                  </span>
                                )}{" "}
                                <span>{option.label}</span>{" "}
                              </div>
                              <div>
                                <BFButton
                                  //   noPadding
                                  appearance="clear-on-white"
                                  size="xs"
                                  onClick={() => {
                                    props.onChange(
                                      props.value.filter(
                                        (id) => id !== valueEntry
                                      )
                                    );
                                  }}
                                >
                                  <BfIcon {...DefaultIcons.CLOSE} size="xxs" />
                                </BFButton>
                              </div>
                            </>
                          );
                        };

                        if (
                          (props as BFAssetChooserGetOptionPromise)
                            .getOptionPromise
                        ) {
                          return (
                            <PromiseLoader
                              promise={async (params) =>
                                await (
                                  props as BFAssetChooserGetOptionPromise
                                ).getOptionPromise(asset)
                              }
                              render={(option) => {
                                return renderOption(option);
                              }}
                            />
                          );
                        } else {
                          const option = (
                            props as BFAssetChooserGetOption
                          ).getOption(asset);

                          return renderOption(option as BFAssetChooserOption);
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            )}

            <BFOverlay
              identifier={id}
              trigger="click"
              placement="bottomStart"
              speaker={
                <div
                  className={`bf-asset-chooser-select-overlay ${
                    props.overlayClassName || ""
                  }`}
                >
                  {props.overlayTopComponent?.(() => {
                    DataBus.emit("WHISPER", {
                      identifier: id,
                      type: "CLOSE",
                    });
                  })}
                  <BFAssetChooser
                    dense
                    //   allowDeselect={props.allowDeselect}
                    searchPlaceholder={i18n.t("Global.Labels.search")}
                    groups={props.groups}
                    assetType={props.assetType}
                    getOption={(props as BFAssetChooserGetOption).getOption}
                    getOptionPromise={
                      (props as BFAssetChooserGetOptionPromise).getOptionPromise
                    }
                    matchQuery={MQ.and(
                      props.matchQuery,
                      when(hasValue(props.value), () =>
                        MQ.nin("_id", props.value)
                      )
                    )}
                    sort={props.sort}
                    focusOnMount
                    height={props.overlayHeight || "50vh"}
                    onChange={(value, asset) => {
                      DataBus.emit("WHISPER", {
                        identifier: id,
                        type: "CLOSE",
                      });
                      props.onChange([...(props.value || []), value]);
                    }}
                  />
                  {props.overlayBottomComponent?.(() => {
                    DataBus.emit("WHISPER", {
                      identifier: id,
                      type: "CLOSE",
                    });
                  })}
                </div>
              }
            >
              <div>
                {props.value?.length > 0 ? (
                  <div className={`__flex __justify-center`}>
                    <BFButton appearance="link" size="xs">
                      {i18n.t(
                        "BFAssetChooserSelectMultiple.add",
                        "Weitere hinzufügen"
                      )}
                    </BFButton>
                  </div>
                ) : (
                  <BFButton
                    disabled={props.disabled}
                    className={classNames(`bf-asset-chooser-select-button`, {
                      "has-value": !!props.value,
                    })}
                  >
                    <div
                      className={classNames({
                        "select-value": true,
                        placeholder: !props.value,
                      })}
                    >
                      {!props.value && (props.placeholder || "-")}
                    </div>
                    <BfIcon size="xxs" {...DefaultIcons.CHEVRON} />
                  </BFButton>
                )}
              </div>
            </BFOverlay>
          </div>
          {/* {props.value && props.detailView && (
            <div className={`asset-detail-view`}>
              <AssetLoader
                assetType={props.assetType}
                id={props.value}
                render={(asset: BaseAsset) => props.detailView(asset)}
              />
            </div>
          )} */}
        </ValidationPopover>
      </LabeledInput>
    </div>
  );
};

export default BFAssetChooserSelectMultiple;
